// Client-specific configuration for the Tessera Autonomy autonomous-flight demo
// — the same templatization seam as the other demos' scenarios. Components read
// from this object and never hard-code client names or narrative text.
//
// Tessera leads with a distinct LAYOUT — a flight record / ops-console strip
// (FlightRecord) whose legs are the actions of ONE sealed inspection mission —
// with the live seal behind a second tab, sharing the same LivePanel + gig
// catalog. A toggle switches between two recorded receipts: a conformant flight
// and one that edges 3 ft over the 400 ft altitude ceiling.

import type { LivePanelCopy } from "@demo/ui/LivePanel.js";
import type { FlightRecordConfig, FlightLeg } from "@demo/ui/FlightRecord.js";

export interface TesseraScenario {
  client: { name: string; domain: string; mark: string };
  product: { name: string; tagline: string };
  disclosure: string;
  masthead: string;
  intro: { headline: string; body: string };
  // Overview tab: the flight record. Scenario bundleIds key into FLIGHT_BUNDLES
  // (apps/flight-demo/src/data.ts).
  record: FlightRecordConfig;
  liveCta: { label: string; sub: string };
  live: LivePanelCopy;
}

// The conformant flight: takeoff, A-2, cross to A-3, three sub-captures in A-3,
// back to A-2, land — every leg within the authorized sectors and under 400 ft.
const CONFORMANT_LEGS: FlightLeg[] = [
  {
    icon: "dispatch",
    phase: "Dispatch",
    title: "Takeoff from base",
    tele: "base BASE-07 · mission FLT-2026-0814-17 · mandate flight_execution bound at genesis"
  },
  {
    icon: "sector",
    phase: "Airspace",
    title: "Entered Sector A-2",
    tele: "sector A-2 (authorized) · 240 ft AGL · within ceiling"
  },
  {
    icon: "sector",
    phase: "Airspace",
    title: "Departed A-2 → entered A-3",
    tele: "sectors A-2 → A-3 (authorized) · peak 382 ft AGL · within ceiling"
  },
  {
    icon: "capture",
    phase: "Inspection · 1 of 3",
    title: "Capture: Tower 42-A",
    tele: "96 frames · sector A-3 · 371 ft AGL · imagery sealed by hash"
  },
  {
    icon: "capture",
    phase: "Inspection · 2 of 3",
    title: "Capture: Span 42–43",
    tele: "128 frames · sector A-3 · 388 ft AGL · imagery sealed by hash"
  },
  {
    icon: "capture",
    phase: "Inspection · 3 of 3",
    title: "Capture: Tower 43-B",
    tele: "94 frames · sector A-3 · 366 ft AGL · imagery sealed by hash"
  },
  {
    icon: "sector",
    phase: "Airspace",
    title: "Departed A-3 → returned to A-2",
    tele: "sectors A-3 → A-2 (authorized) · 356 ft AGL · within ceiling"
  },
  {
    icon: "recovery",
    phase: "Recovery",
    title: "Returned & landed",
    tele: "base BASE-07 · 318 frames sealed · mission closed"
  }
];

// The off-mandate flight: identical, except the A-2 → A-3 crossing peaks 3 ft
// over the 400 ft ceiling — the record shows it, the mandate flags it.
const VIOLATION_LEGS: FlightLeg[] = CONFORMANT_LEGS.map((leg, i) =>
  i === 2
    ? {
        ...leg,
        tele: "sectors A-2 → A-3 (authorized)",
        flag: "peak 403 ft AGL, 3 ft over the 400 ft ceiling"
      }
    : leg
);

export const TESSERA: TesseraScenario = {
  client: {
    name: "Tessera Autonomy",
    domain: "tesseraautonomy.com",
    mark: "T"
  },
  product: {
    name: "Sequesign",
    tagline: "Verifiable receipts for agent actions"
  },
  disclosure:
    "Fictional demonstration. Tessera Autonomy, the mission, and the flight shown are not real. Captured imagery is sealed only as a hash. The receipt carries no imagery. Each flight record is a real Sequesign receipt, sealed by an in-process demo witness and verified live in your browser.",
  masthead: "Every mission, provable",
  record: {
    eyebrow: "See it in 15 seconds",
    headline:
      "An autonomous drone flew this inspection. It says it stayed in authorized airspace. Don't trust it. Verify the flight record.",
    sub: "Tessera's drone flew an infrastructure inspection under a flight mandate: authorized airspace sectors and an altitude ceiling. Every leg and every capture is sealed into one tamper-evident record. Once it lands, the record is the only witness, so recompute it yourself:",
    recordTitle: "Flight record: powerline inspection, Segment 42",
    recordSub: "mandate flight_execution · FLT-2026-0814-17",
    mandate: {
      sectorsLabel: "Authorized sectors",
      sectors: "A-2 · A-3 · N-7",
      ceilingLabel: "Altitude ceiling",
      ceiling: "400 ft AGL"
    },
    scenarios: [
      {
        key: "conformant",
        toggleLabel: "Conformant flight",
        bundleId: "flight-conformant",
        legs: CONFORMANT_LEGS,
        summaryTitle: "Flight record: sealed, ready to verify",
        summaryDetail:
          "Eight legs, bound into one witnessed record. Verify to confirm every signature, the telemetry hashes, and that the flight stayed inside the authorized sectors and under the 400 ft ceiling.",
        expectConformant: true
      },
      {
        key: "violation",
        toggleLabel: "Off-mandate flight",
        bundleId: "flight-violation",
        legs: VIOLATION_LEGS,
        summaryTitle: "Flight record: sealed, ready to verify",
        summaryDetail:
          "Eight legs, bound into one witnessed record. The record already shows a 403 ft peak on the A-2 → A-3 crossing. Verify to confirm the record is authentic, and that the mandate flags that 3 ft breach.",
        expectConformant: false
      }
    ],
    verifyLabel: "Verify the flight record",
    verifyingLabel: "Verifying…",
    reverifyLabel: "Verify again",
    browserNote:
      "The whole flight is one receipt. Verifying it checks every leg together. Switch to the off-mandate flight to see the same record when the drone edges just 3 ft over the ceiling: still authentic, honestly recording conformant: false. The mandate catches the breach to the foot; the record doesn't round it down."
  },
  liveCta: {
    label: "Run a real seal with your own keys",
    sub: "Presenter mode: seal a brand-new flight record against the real witness, end to end, and verify it here."
  },
  intro: {
    headline: "Every autonomous flight, on the record.",
    body: "Tessera's drone flies infrastructure-inspection missions under a flight mandate: the authorized airspace sectors and an altitude ceiling. Enter a mission and run it: the agent seals a real Sequesign flight record server-side (dispatch → sector legs → sub-captures → recovery), then you verify it right here in your browser. Fly into a restricted sector, or edge over the ceiling, and the sealed record honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. Captured imagery is only hashed, never sealed."
  },
  live: {
    title: "Live run: real seal, real witness",
    intro: [
      "Enter a mission and run it: the agent executes a real Sequesign session with your own agent key, seals the flight record leg by leg, and gets each action countersigned by a real witness, then verifies it here in your browser.",
      "The result is verified in EXTERNAL trust-anchor mode: the witness's published keys are fetched from its well-known endpoint (directly, or relayed unchanged through this site) and used as the trust anchor, so this is an independent third-party check, not an integrity self-check.",
      "The flight mandate binds the authorized sectors (A-2, A-3, N-7) and a 400 ft AGL ceiling, enforced on every leg. Edge the peak altitude over 400 ft (e.g. 403), or pick the restricted “R-1” sector, and the sealed record honestly records conformant: false. It still verifies as authentic; the breach is recorded, not hidden. Captured imagery is hashed into the receipt, never sealed raw."
    ],
    contactNote:
      "Live mode contacts the real witness using the keys you enter below. Nothing else on this page makes a network call to that service.",
    keyNote:
      "Keys are used only for this run, in your browser and the request; nothing is stored. They live in React state only, are never written to localStorage, and are cleared when you leave the page.",
    runLabel: "Seal the flight record",
    runningLabel: "Sealing…",
    fields: {
      agentPem: "Agent private key (Ed25519 PEM)",
      witnessBaseUrl: "Witness base URL",
      witnessApiKey: "Witness API key (optional)",
      llmApiKey: "Anthropic API key"
    }
  }
};
